/**
 * Fleet reporting catalog — the Technical Health registry read as a CATALOG only:
 * what checks exist, what they're called, and where their answer comes from.
 *
 * Deliberately does NOT carry tier / gapKind / compliance / addon fields. Fleet
 * reporting has no scoring or plan-filtering machinery — see
 * cma.wiki/Architecture/Fleet-Reporting.md ("Key architectural decision").
 *
 * Two kinds of check live here:
 *   'audit'  — backed by a producer fact, queried from cma/audit/report. Keyed on
 *              auditRunId, not auditType: one runId can span several producers
 *              (DATTO covers datto-server + datto-desktop).
 *   'manual' — a value a vCIO recorded by hand, stored as an accountprofile
 *              directive. No producer, no fact, no daily trail; rows for these are
 *              generated per account by manualRows.js.
 *
 * valueKind is deliberately NOT carried across: the stored directive already has
 * its own value_type, so the type is read from the data rather than the registry.
 */
import { CHECKS, DOMAINS, groupBySubcategory } from '../Customer/health/healthRegistry';

export const FLEET_DOMAINS = DOMAINS;

const base = (c) => ({
  key: c.key,
  label: c.label,
  domain: c.domain,
  subcategory: c.subcategory,
});

// ONE pass over CHECKS, so the catalog keeps the registry's order. Building this
// as [...audit, ...manual] instead would sort every automated check ahead of
// every attested one, and the picker would then disagree with the Technical
// Health tab about the order of checks inside a subcategory.
export const FLEET_CHECKS = CHECKS.reduce((out, c) => {
  if (c.source.type === 'audit' && c.source.checkKey) {
    out.push({ ...base(c), kind: 'audit', auditRunId: c.source.auditRunId, checkKey: c.source.checkKey });
  } else if (c.source.type === 'manual') {
    out.push({ ...base(c), kind: 'manual', manualKey: c.key });
  }
  // Anything else (a 'pending' producer) has no way to be reported on yet.
  return out;
}, []);

export const AUDIT_CHECKS = FLEET_CHECKS.filter((c) => c.kind === 'audit');
export const MANUAL_CHECKS = FLEET_CHECKS.filter((c) => c.kind === 'manual');

export const FLEET_RUN_IDS = [...new Set(AUDIT_CHECKS.map((c) => c.auditRunId))].sort();

// Registry checks the report still cannot offer — only 'pending' ones, whose
// producer is not live yet. Derived, not hardcoded.
export const UNREPORTABLE_CHECK_COUNT = CHECKS.length - FLEET_CHECKS.length;

// Look up by the registry key the picker hands back.
export const fleetCheckByKey = Object.fromEntries(FLEET_CHECKS.map((c) => [c.key, c]));

// Human label for a REPORT ROW's checkKey, which is not the registry key: audit
// rows carry the producer's jobResults key (_rows.py), manual rows carry the
// registry key because there is no producer key to carry (manualRows.js). One
// flat map covers both -- verified collision-free across all 101 checks.
export const fleetLabelByRowKey = Object.fromEntries(
  FLEET_CHECKS.map((c) => [c.kind === 'audit' ? c.checkKey : c.manualKey, c.label]),
);

// What a selection needs: producer scope for the API call, manual keys for the
// client-side row builder.
export function queryScopeFor(selectedKeys) {
  const picked = selectedKeys.map((k) => fleetCheckByKey[k]).filter(Boolean);
  const audit = picked.filter((c) => c.kind === 'audit');
  const manual = picked.filter((c) => c.kind === 'manual');
  return {
    auditRunIds: [...new Set(audit.map((c) => c.auditRunId))].sort(),
    checkKeys: [...new Set(audit.map((c) => c.checkKey))].sort(),
    manualKeys: [...new Set(manual.map((c) => c.manualKey))].sort(),
  };
}

// domain -> subcategory -> checks, in DOMAINS order, for the picker tree.
// Subcategories come back in first-appearance order via the same helper the
// Technical Health tab uses, so the two read identically.
export function groupedFleetChecks() {
  return FLEET_DOMAINS
    .map((d) => ({
      ...d,
      subcategories: groupBySubcategory(FLEET_CHECKS.filter((c) => c.domain === d.key))
        .map((g) => ({ label: g.sub, checks: g.items })),
    }))
    .filter((d) => d.subcategories.length > 0);
}
